const RUNYI_PRODUCTION_ORIGIN = "https://runyi-movie-creative-engine.pages.dev";
const MARKER_URL = "/runyi-deployment-marker.json";
const SERVICE_WORKER_URL = "/runyi-runtime-sw.js";
const REVISION_STORAGE_KEY = "runyi:runtime-source-commit";
const PENDING_REVISION_KEY = "runyi:runtime-pending-source-commit";
const SCROLL_STORAGE_KEY = "runyi:runtime-refresh-scroll-y";
const CHECK_INTERVAL_MS = 15_000;

let checkInFlight = false;
let updateInFlight = false;
let intervalId: number | null = null;

function safeGet(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
}

function safeRemove(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {}
}

function restoreScrollAfterRuntimeRefresh() {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (!raw) return;
    window.sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    const y = Number(raw);
    if (!Number.isFinite(y)) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "auto" }));
    });
  } catch {}
}

function userIsEditing() {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  if (active.isContentEditable) return true;
  return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement;
}

async function fetchCurrentRevision() {
  const response = await fetch(`${MARKER_URL}?t=${Date.now()}`, {
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`deployment marker ${response.status}`);
  const marker = (await response.json()) as { source_commit?: unknown; environment?: unknown };
  if (marker.environment !== "production") return null;
  return typeof marker.source_commit === "string" && marker.source_commit.length >= 7
    ? marker.source_commit
    : null;
}

async function waitUntilSafeToReload() {
  if (document.visibilityState !== "visible") {
    await new Promise<void>((resolve) => {
      const onVisibility = () => {
        if (document.visibilityState !== "visible") return;
        document.removeEventListener("visibilitychange", onVisibility);
        resolve();
      };
      document.addEventListener("visibilitychange", onVisibility);
    });
  }

  while (userIsEditing()) {
    await new Promise((resolve) => window.setTimeout(resolve, 500));
  }
}

async function prepareRuntimeAndReload(registration: ServiceWorkerRegistration, revision: string) {
  if (updateInFlight) return;
  updateInFlight = true;
  safeSet(PENDING_REVISION_KEY, revision);

  try {
    const worker = navigator.serviceWorker.controller || registration.active || registration.waiting;
    if (!worker) throw new Error("runtime service worker unavailable");

    const ready = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        navigator.serviceWorker.removeEventListener("message", onMessage);
        reject(new Error("runtime cache preparation timed out"));
      }, 12_000);

      function onMessage(event: MessageEvent) {
        const data = event.data || {};
        if (data.revision !== revision) return;
        if (data.type === "RUNYI_RUNTIME_UPDATE_READY") {
          window.clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener("message", onMessage);
          resolve();
        } else if (data.type === "RUNYI_RUNTIME_UPDATE_FAILED") {
          window.clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener("message", onMessage);
          reject(new Error(String(data.reason || "runtime update failed")));
        }
      }

      navigator.serviceWorker.addEventListener("message", onMessage);
      worker.postMessage({ type: "RUNYI_PREPARE_RUNTIME_UPDATE", revision });
    });

    await ready;
    safeSet(REVISION_STORAGE_KEY, revision);
    safeRemove(PENDING_REVISION_KEY);
    await waitUntilSafeToReload();

    try {
      window.sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
    } catch {}

    window.location.reload();
  } catch {
    // Keep the current working runtime visible. The next foreground/interval
    // check retries without interrupting the user's current session.
    updateInFlight = false;
  }
}

async function checkForRuntimeUpdate(registration: ServiceWorkerRegistration) {
  if (checkInFlight || updateInFlight) return;
  checkInFlight = true;
  try {
    const revision = await fetchCurrentRevision();
    if (!revision || revision === "local-unverified") return;

    const stored = safeGet(REVISION_STORAGE_KEY);
    if (!stored) {
      safeSet(REVISION_STORAGE_KEY, revision);
      safeRemove(PENDING_REVISION_KEY);
      return;
    }

    if (revision !== stored) {
      await prepareRuntimeAndReload(registration, revision);
    }
  } catch {
    // Network/version checks are advisory. The cached runtime stays active.
  } finally {
    checkInFlight = false;
  }
}

export async function startRunyiRuntimeUpdates() {
  restoreScrollAfterRuntimeRefresh();

  if (window.location.origin !== RUNYI_PRODUCTION_ORIGIN) return;
  if (!("serviceWorker" in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, {
      scope: "/",
      updateViaCache: "none",
    });
    await navigator.serviceWorker.ready;

    void checkForRuntimeUpdate(registration);

    const foregroundCheck = () => {
      if (document.visibilityState === "visible") void checkForRuntimeUpdate(registration);
    };

    window.addEventListener("focus", foregroundCheck);
    document.addEventListener("visibilitychange", foregroundCheck);

    if (intervalId !== null) window.clearInterval(intervalId);
    intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") void checkForRuntimeUpdate(registration);
    }, CHECK_INTERVAL_MS);
  } catch {
    // Service worker failure must never block RUNYI. The native shell's
    // bundled recovery runtime remains the final fallback.
  }
}
